import { FC, useId, useMemo, useState } from 'react';
import Button from '../../atoms/Button';
import { confirmVariantOf } from '../../atoms/Button/confirmVariant';
import Modal from '../../atoms/Modal';
import TraceJournalLink from '../../atoms/TraceJournalLink';
import './MessageModal.scss';
import type { IModalMessage } from './IModalMessage';
import { TRACE_ID_LABEL } from '@srm/core/errors';
import { joinIds } from '../../context/FieldA11yContext';
import { pluralize } from '../../lib/pluralize';
import { extractTextFromHTML } from '../../lib/html';
import { MessageToneHeader, MessageToneList, uniqueMessages } from '../MessageTone';

/** Короткий текст — окно поуже: строка не растягивается на всю ширину. */
const COMPACT_TEXT_LENGTH = 240;

const defaultTitle = (type: IModalMessage['type'], count: number) => {
    switch (type) {
        case 'error':
            return count > 1 ? `${count} ${pluralize(count, ['ошибка', 'ошибки', 'ошибок'])}` : 'Ошибка';
        case 'success':
            return 'Готово';
        case 'info':
            return 'Информация';
        default:
            return 'Внимание';
    }
};

export interface IMessageModalProp {
    /** Данные уведомление */
    item: IModalMessage;
    /** Закрыть уведомление */
    remove?: (id: number) => void;
}

const MessageModal: FC<IMessageModalProp> = ({ item, remove }: IMessageModalProp) => {
    const onRemove = () => {
        item.id && remove && remove(item.id);
    };

    // Без Clipboard API (небезопасный контекст) кнопка бессмысленна — код остаётся доступен выделением.
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    const [copyState, setCopyState] = useState<'idle' | 'done' | 'failed'>('idle');

    const copyTraceId = () => {
        if (!item.traceId || !clipboard) return;

        // Отказ реален: страница без фокуса, запрет Permissions-Policy. Молчать нельзя —
        // человек уйдёт в поддержку без кода.
        clipboard.writeText(item.traceId).then(
            () => setCopyState('done'),
            () => setCopyState('failed'),
        );
    };

    const copyLabel =
        copyState === 'done' ? 'Скопировано' : copyState === 'failed' ? 'Не удалось скопировать' : 'Копировать';

    // описание окна — тексты сообщений (код обращения и кнопка копирования — нет)
    const messageId = useId();
    const messages = useMemo(() => uniqueMessages(Array.isArray(item.message) ? item.message : []), [item.message]);
    const describedBy = joinIds(...messages.map((_, index) => `${messageId}-${index}`));

    const onClose = () => {
        item.closeBtnClick && item.closeBtnClick();
        onRemove();
    };

    // Крестик, Escape и фон: у окна-вопроса — свой исход (`dismissClick`), иначе — то же, что закрывающая кнопка.
    const onDismiss = () => {
        if (item.dismissClick) item.dismissClick();
        else item.closeBtnClick && item.closeBtnClick();
        onRemove();
    };

    const tone = item.type && item.type !== 'default' ? item.type : 'info';
    const titleId = useId();
    const title = item.title || defaultTitle(item.type, messages.length);
    // Своя главная кнопка (вид по умолчанию — primary) — закрывающая уходит во вторичные.
    const hasPrimaryButton = item.buttons?.some(
        (button) => (button.variant ?? confirmVariantOf(button.tone)) === 'primary',
    );
    const closeBtnType = item.closeBtnType ?? (hasPrimaryButton ? 'light' : undefined);
    const compact = useMemo(
        () => messages.length === 1 && extractTextFromHTML(messages[0]).length <= COMPACT_TEXT_LENGTH,
        [messages],
    );

    const footer = (
        <div className={'message-modal__footer'}>
            {item.buttons &&
                item.buttons.map((button, i) => {
                    return (
                        <Button
                            onClick={() => {
                                onRemove();
                                button.onClick();
                            }}
                            key={i}
                            size="sm"
                            variant={button.variant ?? confirmVariantOf(button.tone)}
                            tone={button.tone}
                        >
                            {button.title}
                        </Button>
                    );
                })}

            <Button onClick={onClose} size="sm" variant={closeBtnType}>
                {item.closeBtnTitle ? item.closeBtnTitle : 'Ок'}
            </Button>
        </div>
    );

    return (
        <Modal
            size={compact ? 'm' : 'l'}
            onClose={onDismiss}
            closeOnEscape
            footer={footer}
            header={<MessageToneHeader tone={tone} title={title} id={titleId} />}
            aria-labelledby={titleId}
            aria-describedby={describedBy}
        >
            <div className="message-modal__container message-tone__body">
                <MessageToneList messages={messages} idPrefix={messageId} />

                {item.traceId && (
                    <p className="message-modal__trace">
                        {TRACE_ID_LABEL}: <code className="message-modal__trace-value">{item.traceId}</code>
                        {clipboard && (
                            <button
                                type="button"
                                className="message-modal__trace-copy"
                                title="Скопировать код обращения"
                                onClick={copyTraceId}
                            >
                                {copyLabel}
                            </button>
                        )}
                        <TraceJournalLink traceId={item.traceId} />
                    </p>
                )}
            </div>
        </Modal>
    );
};

export default MessageModal;
